/**
 * Cartesia streaming TTS over a warm WebSocket — raw-delta continuations (prosody stitched
 * server-side, no sentence chunker), per-context cancel (barge-in), stale-context filtering.
 * Portable: browser/Bun/Node. Auth: static key (server) or BE-minted access token (browser).
 *
 * Browser auth: NEVER ship the real key — your backend mints a short-lived access token
 * (Cartesia `POST /access-tokens`, sent with the real X-API-Key) and the client uses
 * `auth: () => fetch('/your-be/cartesia-token').then(r => r.text())` + `authMode: 'token'`.
 */
import { forComponent } from '../logging';
import { type AuthProvider, resolveAuth, TTS_SAMPLE_RATE } from './types';

const log = forComponent('CartesiaTTS');
const now = () => performance.now();

export class CartesiaTTSOptions {
  auth: AuthProvider = '';
  voiceId = '';
  model = 'sonic-3.5';
  /** 'apiKey' (server/CLI) → `api_key=` URL param; 'token' (browser, BE-minted) → `access_token=`. */
  authMode: 'apiKey' | 'token' = 'apiKey';
}

export class CartesiaTTS {
  public options: CartesiaTTSOptions;
  private ws!: WebSocket;
  private ctxSeq = 0;
  public ctxId = '';
  public onAudio: (chunk: Uint8Array) => void = () => {};
  public onDone: () => void = () => {};
  public firstAudioAt = 0;
  /** Circuit breaker: consecutive error count + down flag. */
  private consecutiveErrors = 0;
  private consecutiveOk = 0;
  private down = false;
  private downAt = 0;
  private probeTimer: ReturnType<typeof setInterval> | null = null;
  private static readonly CB_THRESHOLD = 3;        // open after 3 consecutive errors
  private static readonly CB_RECOVER_OK = 2;        // close only after 2 consecutive good frames (no single-frame flap)
  private static readonly CB_PROBE_MS = 30_000;

  constructor(options?: Partial<CartesiaTTSOptions>) {
    this.options = { ...new CartesiaTTSOptions(), ...options };
  }

  private closed = false;
  private connecting: Promise<void> | null = null;

  async connect(): Promise<void> {
    this.closed = false;
    this.connecting = this.doConnect();
    await this.connecting;
    this.connecting = null;
  }

  private async doConnect(): Promise<void> {
    const key = await resolveAuth(this.options.auth); // per-connect: temp tokens expire
    const param = this.options.authMode === 'token' ? 'access_token' : 'api_key';
    this.ws = new WebSocket(`wss://api.cartesia.ai/tts/websocket?cartesia_version=2026-03-01&${param}=${key}`);
    await new Promise<void>((res, rej) => {
      this.ws.onopen = () => res();
      this.ws.onerror = (e: any) => rej(new Error(`cartesia ws: ${e.message || 'connect failed'}`));
    });
    this.ws.onclose = (ev: any) => {
      log.warn(`cartesia ws closed (${ev.code} ${ev.reason || ''})`);
      if (!this.closed) { this.connecting = this.doConnect().catch((e) => log.error(`cartesia reconnect failed: ${e.message}`)); }
    };
    this.ws.onmessage = (ev) => {
      const m = JSON.parse(String(ev.data));
      if (m.context_id && m.context_id !== this.ctxId) return; // stale context (post-cancel stragglers)
      if (m.type === 'chunk' && m.data) {
        this.consecutiveErrors = 0;
        this.markRecovered();
        if (!this.firstAudioAt) this.firstAudioAt = now();
        this.onAudio(base64ToBytes(m.data));
      } else if (m.type === 'done') {
        this.consecutiveErrors = 0;
        this.markRecovered();
        this.onDone();
      }
      else if (m.type === 'error') {
        if (/already been cancelled|does not exist/.test(m.message || '')) return;
        this.consecutiveErrors++;
        if (!this.down && this.consecutiveErrors >= CartesiaTTS.CB_THRESHOLD) {
          this.down = true;
          this.downAt = now();
          this.consecutiveOk = 0;
          log.warn(`TTS circuit breaker open — ${this.consecutiveErrors} consecutive errors, switching to text-only`);
          this.onDone(); // release any pending drain
          this.startProbe();
        } else if (!this.down) {
          log.warn(`cartesia: ${JSON.stringify(m)}`);
        }
      }
    };
  }

  /** Close the breaker only after CB_RECOVER_OK consecutive good frames, so a single straggler chunk
   *  after a 503 burst doesn't flap open→recover in <1s. A sub-2s down-window is a transient blip → debug. */
  private markRecovered(): void {
    if (!this.down) return;
    if (++this.consecutiveOk < CartesiaTTS.CB_RECOVER_OK) return;
    this.down = false;
    this.consecutiveOk = 0;
    this.stopProbe();
    const downMs = this.downAt ? now() - this.downAt : 0;
    (downMs < 2000 ? log.debug : log.info)(`TTS recovered${downMs ? ` (down ${downMs}ms)` : ''}`);
  }

  /** Ensure the WS is open before sending — reconnects if idle-closed. */
  private async ensureConnected(): Promise<void> {
    if (this.connecting) await this.connecting;
    if (this.ws?.readyState !== WebSocket.OPEN) await this.connect();
  }
  newContext(): string {
    this.ctxId = `ctx-${++this.ctxSeq}`;
    this.firstAudioAt = 0;
    return this.ctxId;
  }
  private frame(transcript: string, cont: boolean): string {
    return JSON.stringify({
      model_id: this.options.model,
      transcript,
      voice: { mode: 'id', id: this.options.voiceId },
      output_format: { container: 'raw', encoding: 'pcm_s16le', sample_rate: TTS_SAMPLE_RATE },
      context_id: this.ctxId,
      continue: cont,
    });
  }
  speak(text: string, cont: boolean): void {
    if (this.down) return;
    // An empty continuation has nothing to synthesize and Cartesia hard-400s on it ("transcript must
    // not be empty when continue is true unless flushing"). Deltas that sanitize to nothing (a chunk of
    // pure markdown/whitespace via forSpeech) hit this — drop them. The real flush is end()'s cont=false.
    if (cont && !text) return;
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(this.frame(text, cont));
    else void this.ensureConnected().then(() => this.ws?.readyState === WebSocket.OPEN && this.ws.send(this.frame(text, cont)));
  }
  end(): void {
    if (this.down) { this.onDone(); return; }
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(this.frame('', false));
  }
  cancel(): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ context_id: this.ctxId, cancel: true }));
  }
  private startProbe(): void {
    if (this.probeTimer) return;
    this.probeTimer = setInterval(() => {
      if (!this.down) { this.stopProbe(); return; }
      this.consecutiveErrors = 0;
      // bypass the `down` guard in speak() — send a probe frame directly
      this.newContext();
      if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(this.frame('.', false));
    }, CartesiaTTS.CB_PROBE_MS);
    (this.probeTimer as any).unref?.();
  }
  private stopProbe(): void {
    if (this.probeTimer) { clearInterval(this.probeTimer); this.probeTimer = null; }
  }
  close(): void {
    this.closed = true;
    this.stopProbe();
    if (this.ws) this.ws.onclose = null as any;
    this.ws?.close();
  }
}

/** atob-based for browsers, Buffer for Bun/Node — both produce a Uint8Array. */
function base64ToBytes(b64: string): Uint8Array {
  if (typeof Buffer !== 'undefined') return Buffer.from(b64, 'base64');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
